import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { api, errMsg, fmtDate } from "../../lib/api";
import { useAuth, canWrite } from "../../context/AuthContext";
import { ConsultantsCard } from "./Consultants";

const ROLES = { org_admin: "Organisation Administrator", manager: "Manager", user: "Standard User", read_only: "Read-Only User" };

const Stat = ({ label, value, testid }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5"><p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p><p className="mt-2 text-3xl font-bold font-heading" data-testid={testid}>{value}</p></div>
);

function InviteDialog({ onClose, onDone }) {
  const [f, setF] = useState({ name: "", email: "", role: "user" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/org/users/invite", f);
      toast.success(`Invitation sent to ${f.email}`);
      onDone();
      onClose();
    } catch (e2) {
      toast.error(errMsg(e2));
    }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="invite-dialog">
        <DialogHeader><DialogTitle className="font-heading">Invite a staff member</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <label className="block text-sm font-medium">Name<input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="field mt-1.5" data-testid="invite-name-input" /></label>
          <label className="block text-sm font-medium">Email<input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="field mt-1.5" data-testid="invite-email-input" /></label>
          <label className="block text-sm font-medium">Role<select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className="field mt-1.5" data-testid="invite-role-select">{Object.entries(ROLES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
          <p className="text-xs text-slate-500">Each user receives their own login. Shared accounts are not permitted.</p>
          <button disabled={busy} className="w-full bg-sky-600 text-white font-semibold py-2.5 rounded-md" data-testid="invite-submit-btn">{busy ? "Sending…" : "Send invitation"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function UsersPage() {
  const { session } = useAuth();
  const [users, setUsers] = useState([]);
  const [org, setOrg] = useState(null);
  const [invite, setInvite] = useState(false);
  const isAdmin = session.user.role === "org_admin";
  const load = () => {
    api.get("/org/users").then((r) => setUsers(r.data));
    api.get("/org").then((r) => setOrg(r.data));
  };
  useEffect(() => {
    load();
  }, []);
  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const full = org && org.seats_available <= 0;
  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="users-page">
      <div className="flex flex-col sm:flex-row justify-between sm:items-end gap-4">
        <div><p className="eyebrow">Administration</p><h1 className="mt-2 text-3xl font-bold tracking-tight">Users &amp; Licences</h1></div>
        {isAdmin && (
          <div className="flex gap-2">
            <Link to="/app/billing" className="border border-slate-300 bg-white px-4 py-2.5 rounded-md text-sm font-semibold" data-testid="users-buy-seats-link">Purchase additional users</Link>
            <button disabled={!canWrite(session) || full} onClick={() => setInvite(true)} title={full ? "All licences allocated" : ""} className="inline-flex items-center gap-2 bg-sky-600 disabled:bg-slate-300 text-white px-4 py-2.5 rounded-md text-sm font-semibold" data-testid="users-invite-btn"><UserPlus className="w-4 h-4" />Invite user</button>
          </div>
        )}
      </div>
      {org && (
        <div className="grid grid-cols-3 gap-4">
          <Stat label="Purchased licences" value={org.free_seats ? `${org.seats_purchased} + ${org.free_seats} free` : org.seats_purchased} testid="seats-purchased" />
          <Stat label="Allocated (active)" value={org.seats_used} testid="seats-used" />
          <Stat label="Available" value={org.seats_available} testid="seats-available" />
        </div>
      )}
      {org && org.seats_used > org.seat_limit && isAdmin && <p className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-md p-3" data-testid="seats-over-limit-note">You have {org.seats_used} active users but {org.seat_limit} licences. Please add licences on the Billing page or deactivate {org.seats_used - org.seat_limit} user(s).</p>}
      {full && isAdmin && <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="seats-full-note">All purchased licences are allocated. Purchase additional users or deactivate a user to invite someone new.{org?.access?.state === "trial" && " The free trial includes one user."}</p>}
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Last login</th>{isAdmin && <th className="px-4 py-3 text-right">Actions</th>}</tr></thead>
          <tbody>
            {users.map((u) => {
              const self = u.id === session.user.id;
              return (
                <tr key={u.id} className="border-t border-slate-100" data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-3 font-medium">{u.name}{self && <span className="text-xs text-slate-500"> (you)</span>}</td>
                  <td className="px-4 py-3 text-slate-600">{u.email}</td>
                  <td className="px-4 py-3">
                    {isAdmin && !self ? <select value={u.role} onChange={(e) => act(() => api.put(`/org/users/${u.id}`, { role: e.target.value }), "Role updated")} className="field h-8 w-52" data-testid={`user-role-${u.email}`}>{Object.entries(ROLES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select> : ROLES[u.role]}
                  </td>
                  <td className="px-4 py-3">{!u.active ? <span className="text-slate-500">Deactivated</span> : !u.email_verified ? <span className="text-amber-700">Invited</span> : <span className="text-emerald-700">Active</span>}</td>
                  <td className="px-4 py-3 text-slate-600">{fmtDate(u.last_login)}</td>
                  {isAdmin && (
                    <td className="px-4 py-3 text-right whitespace-nowrap space-x-3">
                      {!self && <button onClick={() => act(() => api.put(`/org/users/${u.id}`, { active: !u.active }), u.active ? "User deactivated" : "User reactivated")} className="text-sky-700 font-semibold" data-testid={`user-toggle-${u.email}`}>{u.active ? "Deactivate" : "Activate"}</button>}
                      <button onClick={() => act(() => api.post(`/org/users/${u.id}/reset`), "Access reset email sent")} className="text-sky-700 font-semibold" data-testid={`user-reset-${u.email}`}>Reset access</button>
                      {!self && <button onClick={() => window.confirm(`Remove ${u.name}?`) && act(() => api.delete(`/org/users/${u.id}`), "User removed")} className="text-red-700 font-semibold" data-testid={`user-remove-${u.email}`}>Remove</button>}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {invite && <InviteDialog onClose={() => setInvite(false)} onDone={load} />}
      {isAdmin && <ConsultantsCard org={org} onChange={load} />}
    </div>
  );
}
